import { t } from 'i18next';
import { urlHelper } from 'src/app/helpers';
import { getFormData } from 'src/app/helpers/form.helper';
import {
  infiniteQueryOptions,
  InitialPageParam,
  onQueryStartedCreate,
  onQueryStartedDelete,
  onQueryStartedUpdate,
} from 'src/app/helpers/redux.helper';
import { apiSlice } from 'src/app/redux/api/apiSlice';
import { ImportDataType } from 'src/app/redux/interests/interest-slice.type';
import {
  LocationIdentifiedCreateDataType,
  LocationIdentifiedDataType,
  LocationIdentifiedLookupDataType,
  LocationIdentifiedResultType,
  LocationIdentifiedUpdateDataType,
} from 'src/app/services/interest/Location/location-service.type';
import { finalUrlsConfig } from 'src/configs/url.config';
import { MutationType } from 'src/enums/operation.enum';

export const locationIdentifiedSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLocationIdentified: builder.infiniteQuery<
      LocationIdentifiedResultType,
      LocationIdentifiedLookupDataType | void,
      InitialPageParam
    >({
      infiniteQueryOptions,
      query: ({ pageParam, queryArg }) => {
        return {
          url: finalUrlsConfig().interest.identified.location.lookup,
          params: {
            ...pageParam,
            ...queryArg,
          },
        };
      },

      providesTags: [{ type: 'LocationIdentified', id: 'LIST' }],
    }),
    getLocationsIdentifiedAll: builder.query<LocationIdentifiedResultType, LocationIdentifiedLookupDataType | void>({
      query: (params?: LocationIdentifiedLookupDataType) => ({
        url: finalUrlsConfig().interest.identified.location.lookup,
        params: urlHelper.getLookupQuery(params),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.rows.map(({ id }) => ({
                type: 'LocationIdentified' as const,
                id,
              })),
              { type: 'LocationIdentified', id: 'LIST' },
            ]
          : [{ type: 'LocationIdentified', id: 'LIST' }],
    }),

    // downloadLocationImages: builder.query<
    //   { filename: string; data: ArrayBuffer }[],
    //   { id: string }
    // >({
    //   query: (param: { id: string }) => ({
    //     url: `${finalUrlsConfig().interest.identified.location.images}/${param.id}`,

    //     cache: "no-cache",

    //     responseHandler: async (response) =>
    //       URL.createObjectURL(await response.blob()),
    //   }),
    // }),

    createLocation: builder.mutation<LocationIdentifiedDataType, LocationIdentifiedCreateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.location.create,
        body: getFormData(data, ['pictures'], MutationType.CREATE),
        method: 'POST',
      }),
      onQueryStarted: async (locationData, api) => onQueryStartedCreate(locationData, api, 'getLocationIdentified'),
      invalidatesTags: [
        // { type: 'LocationIdentified', id: 'LIST' },
        { type: 'Interests', id: 'LIST' },
      ],
    }),

    updateLocation: builder.mutation<LocationIdentifiedDataType, LocationIdentifiedUpdateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.location.update,
        body: getFormData(data, ['pictures']),
        method: 'PATCH',
      }),
      onQueryStarted: async ({ id }, api) => onQueryStartedUpdate({ id }, api, 'getLocationIdentified'),
      // invalidatesTags: (_result, _error, data) => [{ type: 'LocationIdentified', id: data.id }],
    }),
    deleteLocation: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `${finalUrlsConfig().interest.identified.location.delete}/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (id, api) => onQueryStartedDelete(id, api, 'getLocationIdentified'),

      // invalidatesTags: (_result, _error, id) => [{ type: 'LocationIdentified', id }],
    }),
    exportLocationIdentifiedExcell: builder.mutation<ArrayBuffer, { ids?: string[] }>({
      query: (data) => ({
        url: finalUrlsConfig().exports.location,
        method: 'POST',
        body: data,
        responseHandler: async (response: any) => {
          if (!response.ok) {
            const errorResponse = await response.json();
            throw new Error(errorResponse?.message || t('errors.defaultErrorText'));
          }
          return response.blob();
        },
        cache: 'no-cache',
      }),
    }),
    importLocation: builder.mutation<void, ImportDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.location.import,
        method: 'POST',
        body: getFormData(data, ['file']),
      }),

      invalidatesTags: [{ type: 'LocationIdentified', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetLocationsIdentifiedAllQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
  useExportLocationIdentifiedExcellMutation,
  useLazyGetLocationsIdentifiedAllQuery,
  useGetLocationIdentifiedInfiniteQuery,
  useImportLocationMutation,
} = locationIdentifiedSlice;
